import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { IconLogout } from "@tabler/icons-react";
import { clsx } from "clsx";
import { Text } from "../typography";
import { UserAvatar } from "./user-avatar";

interface UserMenuProps {
  name: string;
  email: string;
  initials: string;
  entries: UserMenuEntry[];
  onLogout: () => void;
  /** The clickable avatar/trigger rendered in the top nav. */
  trigger: ReactNode;
}

export interface UserMenuEntry {
  label: string;
  icon: ReactNode;
  to: string;
  badgeCount?: number;
}

export function getUserInitials(name?: string | null): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function UserMenu({
  name,
  email,
  initials,
  entries,
  onLogout,
  trigger,
}: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const handlePointer = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };
  const greetingName = name.trim().split(/\s+/).filter(Boolean)[0] ?? "there";

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="User menu"
        className="inline-flex items-center justify-center rounded-full"
      >
        {trigger}
      </button>

      {open ? (
        <div
          role="menu"
          className={clsx(
            "absolute right-0 top-[calc(100%+12px)] z-[80] w-[260px]",
            "overflow-hidden rounded-2xl border border-borderDefault bg-bgWhite",
            "shadow-[0px_20px_25px_-5px_rgba(0,0,0,0.1),0px_8px_10px_2px_rgba(0,0,0,0.1)]",
          )}
        >
          <div className="flex items-start gap-3 border-b border-borderDefault bg-bgLight p-3">
            <UserAvatar initials={initials} size="lg" className="shrink-0" />
            <div className="flex min-w-0 flex-1 flex-col justify-center">
              <Text variant="bodyBold" tone="dark" className="truncate">
                Hello {greetingName},
              </Text>
              <Text variant="bodySm" tone="muted" className="truncate">
                {email}
              </Text>
            </div>
          </div>

          <div className="flex flex-col py-1">
            {entries.map((entry) => (
              <button
                key={entry.label}
                type="button"
                role="menuitem"
                onClick={() => go(entry.to)}
                className="flex items-center gap-3 px-6 py-3 text-left transition-colors hover:bg-bgLight"
              >
                <span className="inline-flex size-4 items-center justify-center text-textBody">
                  {entry.icon}
                </span>
                <span className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <Text variant="bodySmMedium" tone="body" inline className="truncate">
                    {entry.label}
                  </Text>
                  {entry.badgeCount != null && entry.badgeCount > 0 ? (
                    <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-accentError px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
                      {entry.badgeCount > 99 ? "99+" : entry.badgeCount}
                    </span>
                  ) : null}
                </span>
              </button>
            ))}
          </div>

          <div className="border-t border-borderDefault px-6">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
              className="flex h-11 w-full items-center gap-3"
            >
              <span className="inline-flex size-4 items-center justify-center text-accentError">
                <IconLogout size={16} />
              </span>
              <Text variant="bodySmBold" inline className="text-accentError">
                Sign Out
              </Text>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default UserMenu;
